import { useState, useEffect, useCallback, useRef } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { 
  Link2, RefreshCw, Key, AlertTriangle, Check, Copy, ChefHat, Play, Loader2, 
  Package, Users, FileText, Building2, Inbox, UserCheck, CreditCard, X, Download,
  Clock, CheckCircle, XCircle, RotateCcw, Trash2, Eye,
  Save, Layers, UtensilsCrossed
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import PosAktarim from './PosAktarim';
import FixMenuler from './FixMenuler';

export default function PosIntegration() {
  const { company } = useAuth();
  const [activeTab, setActiveTab] = useState('integration');
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showKey, setShowKey] = useState(false);
  
  // API Test Panel state
  const [testLoading, setTestLoading] = useState({});
  const [testResults, setTestResults] = useState({});
  
  // Outbox state
  const [outbox, setOutbox] = useState({ pending: [], failed: [] });
  const [outboxLoading, setOutboxLoading] = useState(false);
  const [processingOutbox, setProcessingOutbox] = useState(false);
  const [processingTicket, setProcessingTicket] = useState(null);
  
  // Customer mapping state
  const [mappingData, setMappingData] = useState({ mappings: [], unmapped: [], suppliers: [] });
  const [mappingLoading, setMappingLoading] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState({});
  
  // Pending payments state
  const [pendingPayments, setPendingPayments] = useState({ payments: [], summary: {} });
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  
  // Outbox detail dialog state
  const [detailDialog, setDetailDialog] = useState({ open: false, record: null });
  
  // Payment mapping state

  useEffect(() => { fetchStatus(); }, []);
  
  // Auto-refresh outbox every 30 seconds when on outbox tab
  useEffect(() => {
    if (activeTab === 'outbox') {
      fetchOutbox();
      const interval = setInterval(fetchOutbox, 30000);
      return () => clearInterval(interval);
    }
  }, [activeTab]);
  
  useEffect(() => {
    if (activeTab === 'mapping') {
      fetchMappingData();
      fetchPendingPayments();
    }
  }, [activeTab]);


  const fetchStatus = () => {
    api.get('/pos/integration/status').then(r => setStatus(r.data)).catch(() => {});
  };
  
  const fetchOutbox = useCallback(async () => {
    setOutboxLoading(true);
    try {
      const res = await api.get('/pos/outbox/pending');
      setOutbox(res.data);
    } catch (err) {
      console.error('Outbox fetch error:', err);
    } finally {
      setOutboxLoading(false);
    }
  }, []);
  
  const fetchMappingData = async () => {
    setMappingLoading(true);
    try {
      const res = await api.get('/pos/customer-mapping');
      setMappingData(res.data);
    } catch (err) {
      toast.error('Musteri eslestirme verileri yuklenemedi');
    } finally {
      setMappingLoading(false);
    }
  };
  
  const fetchPendingPayments = async () => {
    setPaymentsLoading(true);
    try {
      const res = await api.get('/pos/pending-payments');
      setPendingPayments(res.data);
    } catch (err) {
      toast.error('Bekleyen tahsilatlar yuklenemedi');
    } finally {
      setPaymentsLoading(false);
    }
  };
  

  const generateKey = async () => {
    try {
      const res = await api.post('/pos/integration/generate-key');
      toast.success('API key olusturuldu');
      setStatus(prev => ({ ...prev, api_key: res.data.api_key, has_key: true }));
      setShowKey(true);
    } catch (err) { toast.error('Hata'); }
  };

  const copyKey = () => {
    if (status?.api_key) {
      navigator.clipboard.writeText(status.api_key);
      toast.success('API key kopyalandi');
    }
  };

  const copyCompanyId = () => {
    if (company?.id) {
      navigator.clipboard.writeText(company.id);
      toast.success('Sirket ID kopyalandi');
    }
  };

  const copyUrl = () => {
    const url = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    navigator.clipboard.writeText(url);
    toast.success('ERP URL kopyalandi');
  };
  
  // Outbox actions
  const processAllOutbox = async () => {
    setProcessingOutbox(true);
    try {
      const res = await api.post('/pos/outbox/process?limit=100');
      toast.success(`${res.data.completed} kayit islendi, ${res.data.failed} hata`);
      fetchOutbox();
      fetchStatus();
    } catch (err) {
      toast.error('Islem hatasi');
    } finally {
      setProcessingOutbox(false);
    }
  };
  
  const retryTicket = async (ticketId) => {
    setProcessingTicket(ticketId);
    try {
      await api.post(`/pos/outbox/retry/${ticketId}`);
      toast.success('Kayit yeniden kuyruga alindi');
      fetchOutbox();
    } catch (err) {
      toast.error('Hata');
    } finally {
      setProcessingTicket(null);
    }
  };
  
  const deleteOutboxRecord = async (ticketId) => {
    if (!window.confirm('Bu kaydi silmek istediginize emin misiniz?')) return;
    try {
      await api.delete(`/pos/outbox/${ticketId}`);
      toast.success('Kayit silindi');
      fetchOutbox();
      fetchStatus();
    } catch (err) {
      toast.error('Silme hatasi');
    }
  };
  
  const processSingleOutbox = async (outboxId) => {
    setProcessingTicket(outboxId);
    try {
      const res = await api.post(`/pos/outbox/process/${outboxId}`);
      const result = res.data;
      if (result.status === 'completed') {
        toast.success(`Başarıyla aktarıldı: ${result.result?.success || 0} ürün, ${result.result?.payments_processed || 0} ödeme`);
      } else if (result.status === 'failed') {
        toast.error(`İşlem hatası: ${result.error_message || 'Bilinmeyen hata'}`);
      }
      fetchOutbox();
      fetchStatus();
    } catch (err) {
      const errMsg = err.response?.data?.detail || 'İşlem hatası';
      toast.error(errMsg);
    } finally {
      setProcessingTicket(null);
    }
  };
  
  // Customer mapping actions
  const createMapping = async (posContactId, posCustomerName) => {
    const supplierId = selectedSupplier[posContactId];
    if (!supplierId) {
      toast.error('Lutfen bir cari hesap secin');
      return;
    }
    
    try {
      const res = await api.post('/pos/customer-mapping', {
        pos_contact_id: posContactId,
        supplier_id: supplierId,
        pos_customer_name: posCustomerName
      });
      toast.success(`Eslestirme yapildi. ${res.data.processed_payments?.count || 0} tahsilat otomatik islendi.`);
      setSelectedSupplier(prev => ({ ...prev, [posContactId]: undefined }));
      fetchMappingData();
      fetchPendingPayments();
    } catch (err) {
      toast.error('Eslestirme hatasi');
    }
  };
  
  const deleteMapping = async (mappingId) => {
    if (!window.confirm('Eslestirmeyi kaldirmak istediginize emin misiniz?')) return;
    try {
      await api.delete(`/pos/customer-mapping/${mappingId}`);
      toast.success('Eslestirme kaldirildi');
      fetchMappingData();
    } catch (err) {
      toast.error('Hata');
    }
  };
  
  // Pending payment actions
  const processSinglePayment = async (paymentId) => {
    try {
      const res = await api.post(`/pos/pending-payments/${paymentId}/process`);
      toast.success(`${res.data.amount} TL tahsilat islendi`);
      fetchPendingPayments();
      fetchMappingData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Islem hatasi');
    }
  };

  // API Test function
  const testEndpoint = async (endpointKey, endpoint, label) => {
    if (!status?.api_key) {
      toast.error('Once API key olusturun');
      return;
    }
    
    setTestLoading(prev => ({ ...prev, [endpointKey]: true }));
    setTestResults(prev => ({ ...prev, [endpointKey]: null }));
    
    const startTime = Date.now();
    try {
      const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${baseUrl}${endpoint}`, {
        method: 'GET',
        headers: {
          'X-ERP-API-Key': status.api_key,
          'Content-Type': 'application/json'
        }
      });
      
      const duration = Date.now() - startTime;
      const data = await response.json();
      
      setTestResults(prev => ({
        ...prev,
        [endpointKey]: {
          success: response.ok,
          status: response.status,
          statusText: response.statusText,
          duration,
          data,
          timestamp: new Date().toLocaleTimeString('tr-TR')
        }
      }));
      
      if (response.ok) {
        toast.success(`${label} basarili (${duration}ms)`);
      } else {
        toast.error(`${label} hatasi: ${response.status}`);
      }
    } catch (err) {
      const duration = Date.now() - startTime;
      setTestResults(prev => ({
        ...prev,
        [endpointKey]: {
          success: false,
          error: err.message,
          duration,
          timestamp: new Date().toLocaleTimeString('tr-TR')
        }
      }));
      toast.error(`${label} hatasi: ${err.message}`);
    } finally {
      setTestLoading(prev => ({ ...prev, [endpointKey]: false }));
    }
  };

  if (!status) return <div className="text-center py-8 text-[#8a7e70]">Yukleniyor...</div>;

  // Collect all no-recipe items from logs
  const allNoRecipe = [];
  const seen = new Set();
  (status.logs || []).forEach(log => {
    (log.no_recipe_items || []).forEach(item => {
      if (!seen.has(item.product_id)) {
        seen.add(item.product_id);
        allNoRecipe.push(item);
      }
    });
  });

  return (
    <div data-testid="pos-integration-page">
      <div className="flex items-center gap-3 mb-6">
        <Link2 size={28} className="text-[#2e2d29]" />
        <h1 className="text-3xl font-bold text-[#1a1916] tracking-tight">POS Entegrasyonu</h1>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList>
          <TabsTrigger value="integration">
            <Key size={14} className="mr-2" /> Entegrasyon
          </TabsTrigger>
          <TabsTrigger value="aktarim" data-testid="sekme-aktarim">
            <Layers size={14} className="mr-2" /> Aktarım
          </TabsTrigger>
          <TabsTrigger value="fixmenu" data-testid="sekme-fix-menu">
            <UtensilsCrossed size={14} className="mr-2" /> Fix Menüler
          </TabsTrigger>
          <TabsTrigger value="gunsonu">
            <FileText size={14} className="mr-2" /> Gün Sonu & Stok
          </TabsTrigger>
        </TabsList>

        {/* Üretim noktası → depo eşleştirmesi Aktarım sekmesindeki TEK eşleştirme
            listesine taşındı (kullanıcı: eşleştirme bir kez, tek yerde). */}

        <TabsContent value="integration" className="space-y-6">
          {/* API Key Card */}
          <Card className="bg-[#faf7f2] border-[#d0c4b0]">
            <CardHeader className="border-b border-[#d0c4b0]">
              <CardTitle className="text-lg font-bold text-[#1a1916] flex items-center gap-2">
                <Key size={18} /> Entegrasyon Bilgileri
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="space-y-4">
                {/* ERP URL */}
                <div>
                  <label className="text-xs font-bold text-[#3F3E39] uppercase tracking-wider mb-1.5 block">
                    ERP URL
                  </label>
                  <div className="flex items-center gap-3">
                    <Input
                      type="text"
                      value={process.env.REACT_APP_BACKEND_URL || window.location.origin}
                      readOnly
                      className="bg-white border-[#d0c4b0] font-mono text-sm flex-1"
                    />
                    <Button variant="outline" size="sm" onClick={copyUrl} className="border-[#d0c4b0]">
                      <Copy size={14} className="mr-1" /> Kopyala
                    </Button>
                  </div>
                </div>

                {/* Company ID */}
                <div>
                  <label className="text-xs font-bold text-[#3F3E39] uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                    <Building2 size={12} /> Sirket ID
                  </label>
                  <div className="flex items-center gap-3">
                    <Input
                      type="text"
                      value={company?.id || ''}
                      readOnly
                      className="bg-white border-[#d0c4b0] font-mono text-sm flex-1"
                    />
                    <Button variant="outline" size="sm" onClick={copyCompanyId} className="border-[#d0c4b0]">
                      <Copy size={14} className="mr-1" /> Kopyala
                    </Button>
                  </div>
                </div>

                {/* API Key */}
                <div>
                  <label className="text-xs font-bold text-[#3F3E39] uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                    <Key size={12} /> API Anahtari
                  </label>
                  {status.has_key ? (
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <Input
                          type={showKey ? "text" : "password"}
                          value={status.api_key || ''}
                          readOnly
                          className="bg-white border-[#d0c4b0] font-mono text-sm flex-1"
                        />
                        <Button variant="outline" size="sm" onClick={() => setShowKey(!showKey)} className="border-[#d0c4b0]">
                          {showKey ? 'Gizle' : 'Goster'}
                        </Button>
                        <Button variant="outline" size="sm" onClick={copyKey} className="border-[#d0c4b0]">
                          <Copy size={14} className="mr-1" /> Kopyala
                        </Button>
                      </div>
                      <div className="flex items-center gap-3">
                        <Button variant="outline" size="sm" onClick={generateKey} className="border-[#7a3535] text-[#7a3535]">
                          <RefreshCw size={14} className="mr-1" /> Yeni Key Uret
                        </Button>
                        <p className="text-xs text-[#8a7e70]">Yeni key uretilirse eski key gecersiz olur</p>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-4 bg-[#e8dfd0]/30 rounded-lg">
                      <p className="text-sm text-[#8a7e70] mb-3">Henuz API key olusturulmamis</p>
                      <Button onClick={generateKey} className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916]">
                        <Key size={16} className="mr-2" /> API Key Olustur
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-6 p-3 bg-[#e8dfd0]/50 rounded-lg">
                <p className="text-xs font-bold text-[#3F3E39] mb-2">POS Entegrasyon Endpoint'leri:</p>
                <div className="space-y-1 text-xs text-[#8a7e70] font-mono">
                  <p>Header: X-ERP-API-Key: {'{api_key}'}</p>
                  <p>GET /api/erp/pos/recipes/sync - Receteleri cek</p>
                  <p>GET /api/erp/pos/stock - Stok durumu</p>
                  <p>GET /api/erp/pos/customers - Musteriler</p>
                  <p>POST /api/erp/pos/outbox/ticket - Adisyon/Tahsilat gonder</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* API Test Panel */}
          {status.has_key && (
            <Card className="bg-[#faf7f2] border-[#d0c4b0]">
              <CardHeader className="border-b border-[#d0c4b0]">
                <CardTitle className="text-lg font-bold text-[#1a1916] flex items-center gap-2">
                  <Play size={18} /> API Test Paneli
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div className="flex flex-wrap gap-3">
                    <Button 
                      onClick={() => testEndpoint('recipes', '/api/erp/pos/recipes/sync', 'Receteleri Getir')}
                      disabled={testLoading.recipes}
                      className="bg-[#3d6e4e] text-white hover:bg-[#2e5a3e]"
                    >
                      {testLoading.recipes ? <Loader2 size={14} className="mr-2 animate-spin" /> : <ChefHat size={14} className="mr-2" />}
                      Receteleri Getir
                    </Button>
                    <Button 
                      onClick={() => testEndpoint('stock', '/api/erp/pos/stock', 'Stok Durumunu Getir')}
                      disabled={testLoading.stock}
                      className="bg-[#7a6a2e] text-white hover:bg-[#5f5323]"
                    >
                      {testLoading.stock ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Package size={14} className="mr-2" />}
                      Stok Durumunu Getir
                    </Button>
                    <Button 
                      onClick={() => testEndpoint('customers', '/api/erp/pos/customers', 'Musterileri Getir')}
                      disabled={testLoading.customers}
                      className="bg-[#2e2d29] text-white hover:bg-[#1a1916]"
                    >
                      {testLoading.customers ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Users size={14} className="mr-2" />}
                      Musterileri Getir
                    </Button>
                  </div>

                  {Object.entries(testResults).map(([key, result]) => result && (
                    <div key={key} className={`p-4 rounded-lg border ${result.success ? 'bg-[#3d6e4e]/5 border-[#3d6e4e]/30' : 'bg-[#7a3535]/5 border-[#7a3535]/30'}`}>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-sm text-[#1a1916]">
                            {key === 'recipes' && 'Receteleri Getir'}
                            {key === 'stock' && 'Stok Durumunu Getir'}
                            {key === 'customers' && 'Musterileri Getir'}
                          </span>
                          <span className={`text-xs px-2 py-0.5 rounded ${result.success ? 'bg-[#3d6e4e]/20 text-[#3d6e4e]' : 'bg-[#7a3535]/20 text-[#7a3535]'}`}>
                            {result.success ? 'Basarili' : 'Hata'}
                          </span>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-[#8a7e70]">
                          {result.status && <span>HTTP {result.status}</span>}
                          <span>{result.duration}ms</span>
                        </div>
                      </div>
                      
                      {result.error ? (
                        <div className="p-3 bg-[#7a3535]/10 rounded text-sm text-[#7a3535] font-mono">
                          Hata: {result.error}
                        </div>
                      ) : (
                        <pre className="p-3 bg-[#2e2d29] text-[#f5f0e8] rounded text-xs font-mono overflow-x-auto max-h-40 overflow-y-auto">
                          {JSON.stringify(result.data, null, 2)}
                        </pre>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Ödeme yöntemi eşleştirmesi Aktarım sekmesine taşındı: kart bahşişi,
              paket kredisi ve havale de orada; iki yerde tutulan eşleştirme ayrışırdı. */}
          <Card className="bg-[#faf7f2] border-[#d0c4b0]">
            <CardContent className="p-4 flex items-center justify-between gap-3 text-sm">
              <span className="flex items-center gap-2 text-[#3F3E39]"><CreditCard size={16} /> Ödeme yöntemi → kasa/banka eşleştirmesi artık <b>Aktarım</b> sekmesinde.</span>
              <Button size="sm" variant="outline" onClick={() => setActiveTab('aktarim')}>Aktarım sekmesine git</Button>
            </CardContent>
          </Card>

          {/* Sync Status */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="bg-[#faf7f2] border-[#d0c4b0]">
              <CardContent className="p-4 text-center">
                <p className="text-xs font-bold uppercase text-[#8a7e70]">Son Senkronizasyon</p>
                <p className="text-lg font-bold text-[#1a1916] mt-1">
                  {status.last_sync ? new Date(status.last_sync).toLocaleString('tr-TR') : 'Henuz yok'}
                </p>
              </CardContent>
            </Card>
            <Card className="bg-[#faf7f2] border-[#d0c4b0]">
              <CardContent className="p-4 text-center">
                <p className="text-xs font-bold uppercase text-[#8a7e70]">Outbox Bekleyen</p>
                <p className={`text-lg font-bold mt-1 ${status.outbox_pending > 0 ? 'text-[#7a6a2e]' : 'text-[#3d6e4e]'}`}>
                  {status.outbox_pending || 0}
                </p>
              </CardContent>
            </Card>
            <Card className="bg-[#faf7f2] border-[#d0c4b0]">
              <CardContent className="p-4 text-center">
                <p className="text-xs font-bold uppercase text-[#8a7e70]">Outbox Hatali</p>
                <p className={`text-lg font-bold mt-1 ${status.outbox_failed > 0 ? 'text-[#7a3535]' : 'text-[#3d6e4e]'}`}>
                  {status.outbox_failed || 0}
                </p>
              </CardContent>
            </Card>
            <Card className="bg-[#faf7f2] border-[#d0c4b0]">
              <CardContent className="p-4 text-center">
                <p className="text-xs font-bold uppercase text-[#8a7e70]">Recetesiz Urunler</p>
                <p className={`text-lg font-bold mt-1 ${allNoRecipe.length > 0 ? 'text-[#7a3535]' : 'text-[#3d6e4e]'}`}>
                  {allNoRecipe.length}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* No Recipe Warning */}
          {allNoRecipe.length > 0 && (
            <Card className="bg-[#faf7f2] border-[#d0c4b0] border-l-4 border-l-[#7a3535]">
              <CardHeader className="border-b border-[#d0c4b0]">
                <CardTitle className="text-lg font-bold text-[#7a3535] flex items-center gap-2">
                  <AlertTriangle size={18} /> Recetesiz Urunler
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader><TableRow className="bg-[#e8dfd0] hover:bg-[#e8dfd0]">
                    <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Recipe Code</TableHead>
                    <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Urun Adi</TableHead>
                    <TableHead className="text-[#1a1916] font-bold text-xs uppercase text-right">Islem</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {allNoRecipe.map(item => (
                      <TableRow key={item.product_id} className="border-[#d0c4b0]">
                        <TableCell className="font-mono text-sm text-[#3F3E39]">{item.recipe_code}</TableCell>
                        <TableCell className="font-medium text-[#1a1916]">{item.product_name}</TableCell>
                        <TableCell className="text-right">
                          <Link to="/erp/recipes/new">
                            <Button size="sm" variant="ghost" className="text-[#3d6e4e] hover:bg-[#3d6e4e]/10 text-xs">
                              <ChefHat size={14} className="mr-1" /> Recete Olustur
                            </Button>
                          </Link>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {/* Import Logs */}
          <Card className="bg-[#faf7f2] border-[#d0c4b0]">
            <CardHeader className="border-b border-[#d0c4b0]">
              <CardTitle className="text-lg font-bold text-[#1a1916]">Aktarim Gecmisi</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader><TableRow className="bg-[#e8dfd0] hover:bg-[#e8dfd0]">
                  <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Tarih</TableHead>
                  <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Satis Tarihi</TableHead>
                  <TableHead className="text-[#1a1916] font-bold text-xs uppercase text-right">Toplam</TableHead>
                  <TableHead className="text-[#1a1916] font-bold text-xs uppercase text-right">Basarili</TableHead>
                  <TableHead className="text-[#1a1916] font-bold text-xs uppercase text-right">Recetesiz</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {(status.logs || []).slice(0, 10).map(log => (
                    <TableRow key={log.id} className="border-[#d0c4b0]">
                      <TableCell className="text-sm text-[#3F3E39]">{new Date(log.created_at).toLocaleString('tr-TR')}</TableCell>
                      <TableCell className="text-sm text-[#3F3E39]">{log.sale_date}</TableCell>
                      <TableCell className="text-right font-semibold text-[#1a1916]">{log.total_items}</TableCell>
                      <TableCell className="text-right font-semibold text-[#3d6e4e]">{log.success_count}</TableCell>
                      <TableCell className="text-right font-semibold text-[#7a3535]">{log.no_recipe_items?.length || 0}</TableCell>
                    </TableRow>
                  ))}
                  {!(status.logs || []).length && (
                    <TableRow><TableCell colSpan={5} className="text-center py-8 text-[#8a7e70]">Henuz aktarim yapilmamis</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Gun sonu tahsilat ozeti + negatif stok.
            Excel aktarim sekmesi kaldirildi: satislar adisyon kapanisinda,
            tahsilatlar gun kapanisinda POS'tan dogrudan geliyor. */}
        <TabsContent value="aktarim" className="space-y-6">
          {activeTab === 'aktarim' && <PosAktarim />}
        </TabsContent>

        <TabsContent value="fixmenu" className="space-y-6">
          {activeTab === 'fixmenu' && <FixMenuler />}
        </TabsContent>

        <TabsContent value="gunsonu" className="space-y-6">
          <GunSonuVeStok />
        </TabsContent>
      </Tabs>
    </div>
  );
}

/**
 * Gün sonu tahsilat özeti ve negatif stok.
 *
 * İşletmenin gerçekten bakması gereken iki soru burada:
 *   1. Dün kapanan günün hangi tahsilatı ERP'ye işlenmedi, neden?
 *   2. Hangi hammadde eksiye düştü? (Negatif stok bilerek engellenmiyor —
 *      eksi bakiye bir yerde eksik mal girişi ya da yanlış reçete demek.)
 */
function GunSonuVeStok() {
  const [gunler, setGunler] = useState([]);
  const [eksiStok, setEksiStok] = useState([]);
  const [yukleniyor, setYukleniyor] = useState(false);

  const getir = useCallback(async () => {
    setYukleniyor(true);
    try {
      const [g, s] = await Promise.all([
        api.get('/pos/day-payments/logs'),
        api.get('/pos/negative-stock'),
      ]);
      setGunler(g.data?.logs || []);
      setEksiStok(s.data?.items || []);
    } catch (e) {
      toast.error('Rapor yüklenemedi');
    }
    setYukleniyor(false);
  }, []);

  useEffect(() => { getir(); }, [getir]);

  const para = (n) => (n || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={getir} disabled={yukleniyor}
          className="border-[#d0c4b0]">
          <RefreshCw size={14} className={`mr-1.5 ${yukleniyor ? 'animate-spin' : ''}`} /> Yenile
        </Button>
      </div>

      <Card className="bg-[#faf7f2] border-[#d0c4b0]">
        <CardHeader className="border-b border-[#d0c4b0]">
          <CardTitle className="text-lg font-bold text-[#1a1916] flex items-center gap-2">
            <CreditCard size={18} /> Gün Sonu Tahsilatları
          </CardTitle>
          <p className="text-sm text-[#8a7e70] mt-1">
            Tahsilatlar gün kapanışında tek pakette, nihai haliyle aktarılır. Gün içi
            ödeme düzeltmeleri ayrıca gönderilmez.
          </p>
        </CardHeader>
        <CardContent className="pt-4">
          {gunler.length === 0 ? (
            <p className="text-sm text-[#8a7e70]">Henüz gün kapanışı aktarımı yok.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tarih</TableHead>
                  <TableHead className="text-right">Adisyon</TableHead>
                  <TableHead className="text-right">İşlenen</TableHead>
                  <TableHead className="text-right">İşlenmeyen</TableHead>
                  <TableHead>Neden işlenmedi</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {gunler.map((g) => (
                  <TableRow key={g.id}>
                    <TableCell className="font-medium">{g.date}</TableCell>
                    <TableCell className="text-right">{g.ticket_count}</TableCell>
                    <TableCell className="text-right">{g.processed}</TableCell>
                    <TableCell className="text-right">
                      {g.skipped > 0
                        ? <Badge variant="destructive">{g.skipped}</Badge>
                        : <span className="text-[#8a7e70]">0</span>}
                    </TableCell>
                    <TableCell className="text-xs text-[#8a7e70]">
                      {(g.atlanan_odemeler || []).slice(0, 3).map((a, i) => (
                        <div key={i}>
                          {a.adisyon ? `${a.adisyon}: ` : ''}{a.sebep} ({para(a.tutar)} ₺)
                        </div>
                      ))}
                      {(g.atlanan_odemeler || []).length > 3 && (
                        <div>+{g.atlanan_odemeler.length - 3} kayıt daha</div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card className="bg-[#faf7f2] border-[#d0c4b0]">
        <CardHeader className="border-b border-[#d0c4b0]">
          <CardTitle className="text-lg font-bold text-[#1a1916] flex items-center gap-2">
            <AlertTriangle size={18} /> Negatif Stok
            {eksiStok.length > 0 && (
              <Badge variant="destructive" className="ml-1">{eksiStok.length}</Badge>
            )}
          </CardTitle>
          <p className="text-sm text-[#8a7e70] mt-1">
            Negatif stok bilerek engellenmiyor. Eksi bakiye, bir yerde eksik mal girişi,
            yanlış reçete miktarı ya da yanlış birim olduğunu gösterir.
          </p>
        </CardHeader>
        <CardContent className="pt-4">
          {eksiStok.length === 0 ? (
            <p className="text-sm text-[#8a7e70]">Eksiye düşen stok yok.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Kod</TableHead>
                  <TableHead>Ürün</TableHead>
                  <TableHead>Depo</TableHead>
                  <TableHead className="text-right">Miktar</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {eksiStok.map((s) => (
                  <TableRow key={`${s.product_card_id}-${s.warehouse_name}`}>
                    <TableCell className="font-mono text-xs">{s.code}</TableCell>
                    <TableCell>{s.name}</TableCell>
                    <TableCell className="text-[#8a7e70]">{s.warehouse_name}</TableCell>
                    <TableCell className="text-right font-medium text-red-600">
                      {s.quantity} {s.unit}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
